/* @flow */
import * as React from "react";
import SizeMe from "react-sizeme";
import Theme from "../Theme";
import type { StylesObject } from "../utils/prefixStyles";
import SliderController from "./SliderController";
import SliderPresenter from "./SliderPresenter";

function getPrecision(number) {
  // number of digits after the decimal place
  return (number.toString().split(".")[1] || "").length;
}

// float-safe modulo operator (https://stackoverflow.com/a/31711034)
function isStepValid(min: number, max: number, step: number): boolean {
  const range = max - min;
  if (range % step === 0) {
    return true;
  }
  const precision = Math.max(getPrecision(step), getPrecision(range));
  const rangeInt = parseInt(range.toFixed(precision).replace(".", ""));
  const stepInt = parseInt(step.toFixed(precision).replace(".", ""));
  return rangeInt % stepInt === 0;
}

type Props = {
  hasOffState: boolean,
  formatValue: (number) => string,
  showValueAboveHandle: boolean,
  step: number,
  min: number,
  max: number,
  value: number,
  onChange?: ?(value: number) => void,
  onDragStart?: ?(value: number) => void,
  onDragEnd?: ?(value: number, aborted: ?boolean) => void,
  tooltipStyle?: ?StylesObject,
  isHeat?: ?boolean,
  isCool?: ?boolean,
  size: {
    width: ?number,
  },
};
type State = {
  value: number,
  isBeingDragged: boolean,
};

class SliderContainer extends React.Component<Props, State> {
  static defaultProps = {
    hasOffState: true,
    formatValue(value: number) {
      return String(value);
    },
    showValueAboveHandle: false,
    step: 1,
  };

  state = {
    isBeingDragged: false,
    value: this.props.value,
  };

  _initialValue = this.props.value;

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.value !== this.state.value) {
      this.setState({ value: nextProps.value });
    }
  }

  _getNewValue = (ratio: number): number => {
    const { max, min, step } = this.props;
    const numberOfSteps = (max - min) / step;
    const positionInSteps = Math.round(ratio * numberOfSteps);
    let newValue = min + positionInSteps * step;
    // Trim floating-point cruft
    newValue = parseFloat(newValue.toFixed(getPrecision(step)));
    // Clamp value to range
    return Math.max(min, Math.min(max, newValue));
  };

  _handleStart = (ratio: number) => {
    this._initialValue = this.state.value;
    const newValue = this._getNewValue(ratio);

    this.setState({ isBeingDragged: true, value: newValue });
    if (this.props.onDragStart) {
      this.props.onDragStart(newValue);
    }
    if (this.props.onChange) {
      this.props.onChange(newValue);
    }
  };

  _handleDrag = (ratio: number) => {
    const newValue = this._getNewValue(ratio);

    if (newValue === this.state.value) {
      return;
    }

    this.setState({ value: newValue });
    if (this.props.onChange) {
      this.props.onChange(newValue);
    }
  };

  _handleEnd = (ratio: number) => {
    const newValue = this._getNewValue(ratio);

    this.setState({ isBeingDragged: false, value: newValue });
    if (this.props.onDragEnd) {
      this.props.onDragEnd(newValue);
    }
  };

  _handleAbort = () => {
    this.setState({ value: this._initialValue, isBeingDragged: false });
    if (this.props.onDragEnd) {
      this.props.onDragEnd(this._initialValue, true);
    }
  };

  render() {
    const {
      hasOffState,
      max,
      min,
      step,
      formatValue,
      showValueAboveHandle,
      size,
      tooltipStyle,
      isHeat,
      isCool,
    } = this.props;
    const { value, isBeingDragged } = this.state;

    if (!isStepValid(min, max, step)) {
      console.error(
        `A Slider was rendered with an invalid step ${step} which doesn't evenly divide the range from ${min} to ${max}`
      );
      return null;
    }
    if (max === min) {
      console.error(
        `A Slider was rendered with the same 'min' and 'max' props. The value provided for both was ${max}`
      );
      return null;
    }

    let ratio = (value - min) / (max - min);
    ratio = Math.max(0, Math.min(1, ratio));
    ratio = parseFloat(ratio.toFixed(5));

    return (
      <SliderController
        ratio={ratio}
        onStart={this._handleStart}
        onDrag={this._handleDrag}
        onEnd={this._handleEnd}
        onAbort={this._handleAbort}
      >
        {({ onMouseDown, onTouchStartCapture, captureRef }) => (
          <SliderPresenter
            captureRef={captureRef}
            ariaValues={{ min, max, value }}
            isHeat={isHeat}
            isCool={isCool}
            isBeingDragged={isBeingDragged}
            hasOffState={hasOffState}
            ratio={ratio}
            showValueAboveHandle={showValueAboveHandle}
            fullSliderWidth={size.width}
            tooltipLabel={formatValue(value)}
            tooltipStyle={tooltipStyle}
            onMouseDown={onMouseDown}
            onTouchStartCapture={onTouchStartCapture}
          />
        )}
      </SliderController>
    );
  }
}

export default SizeMe({
  monitorWidth: true,
  noPlaceholder: true,
  refreshRate: Theme.RESPONSIVE_REFRESH_RATE,
})(SliderContainer);



// WEBPACK FOOTER //
// ../nx-components/src/Slider/SliderContainer.jsx